import { getIsApplePlatform, isIosPlatform } from '@/helpers';
import {
    ASCRole,
    AppPlatform,
    AppSettings,
    AppStoreType,
    Automation,
    BetaTestingStyle,
    FixRequestFormFieldSettings,
    Integration,
    IntegrationCapability,
    IntegrationType,
    MergeType,
    PatternToken,
    ReleaseStepBlockingConfig,
    ReleaseStepType,
    ReleaseType,
} from '@/types';
import { Destination } from '@/types/destinations';
import { WorkflowStepGraph } from '@/types/workflow';
import { androidAutomations, iosAutomations, workflowOnlyAutomations } from './automations';
import { androidIntegrations, iosIntegrations } from './integrations';
import { flatMap, unionBy } from 'lodash';
import { DEMO_DESTINATIONS, createWhiteLabelDestinations } from './workflows';
import voyagerAppIcon from '../../assets/app-demo-voyager.png';

export const demoFixRequestFormFieldsSettings: FixRequestFormFieldSettings[] = [
    {
        name: 'Description of fix',
        placeholder: 'What does this fix address and why is it needed for this release?',
        required: true,
        minCharacters: 20,
    },
    {
        name: 'Impact',
        placeholder: 'Who is affected if this fix does not ship?',
        required: false,
    },
];

export const legacyAppSettingsMock = (appId: string, appName: string, platform: AppPlatform) => {
    const destination = {
        'appollo-ios': DEMO_DESTINATIONS.appolloIos,
        'appollo-android': DEMO_DESTINATIONS.appolloAndroid,
        'appollo-react-native-ota': DEMO_DESTINATIONS.appolloReactNativeOta,
    }[appId];

    const destinationInfoByDestinationStepConfigId = destination ? { [destination.stepConfigId]: destination } : {};

    return {
        appName,
        destinations: [destination],
        destinationInfoByDestinationStepConfigId,
        threadFixNotifications: false,
        isWorkflowsMigratedAndEnabled: false,
        branchConfigsById: {},
        tagConfigsById: {},
        featureAffiliationConfigsById: {},
        shouldHideRegisterDevicePrompt: false,
        blackoutDateSets: [],
        signingKeys:
            getIsApplePlatform(platform) ?
                [
                    {
                        id: '42e04296-9e7c-45a1-9cd5-893b7aaf93d7',
                        name: appName,
                        filename: `${appId}.p12`,
                        updatedAt: '2023-10-17T15:53:37.192168+02:00',
                        uploadedBy: 'ssullenberger@demo.com',
                    },
                ]
            :   [
                    {
                        id: 'a29d0be5-23da-4535-b9fb-5a79f564654b',
                        name: `${appName} keystore`,
                        filename: 'keystore.jks',
                        updatedAt: '2023-10-16T12:00:54.860679+02:00',
                        uploadedBy: 'ssullenberger@demo.com',
                    },
                ],
        platform,
        workflowEnabledSteps: {},
        appStoreType: AppStoreType.Apple,
        appImageUrl: 'https://runway-img.s3.us-east-2.amazonaws.com/app-demo-mercury.png',
        betaTestingStyle: BetaTestingStyle.TimeLimitedSoak,
        assumePointReleasesAreHotfixes: false,
        buildDistributionEnabled: true,
        releasePilotRotation: {
            items: [
                {
                    id: 'ssullenberger@demo.com',
                    pilotRotationIndex: 0,
                    releaseVersion: '1.0.0',
                    isReleaseVersionPredicted: false,
                    releaseVersionKickoffDate: null,
                    releaseVersionTargetKickoffDate: '2024-06-20T12:00:00-04:00',
                    isReleaseVersionTargetKickoffDatePredicted: false,
                    overridePilotId: null,
                    releaseType: ReleaseType.Minor,
                    pilotFromScheduleIntegrationEmail: 'ssullenberger@demo.com',
                    isReleaseVersionTargetKickoffDatePredictedFromScheduleIntegration: false,
                    scheduleOnCallShiftStart: null,
                    scheduleOnCallShiftEnd: null,
                },
                {
                    id: 'aearhart@demo.com',
                    pilotRotationIndex: 1,
                    releaseVersion: '1.1.0',
                    isReleaseVersionPredicted: false,
                    releaseVersionKickoffDate: null,
                    releaseVersionTargetKickoffDate: '2024-06-27T12:00:00-04:00',
                    isReleaseVersionTargetKickoffDatePredicted: true,
                    overridePilotId: null,
                    releaseType: ReleaseType.Minor,
                    pilotFromScheduleIntegrationEmail: 'aearhart@demo.com',
                    isReleaseVersionTargetKickoffDatePredictedFromScheduleIntegration: false,
                    scheduleOnCallShiftStart: null,
                    scheduleOnCallShiftEnd: null,
                },
                {
                    id: 'narmstrong@demo.com',
                    pilotRotationIndex: 2,
                    releaseVersion: '1.2.0',
                    isReleaseVersionPredicted: false,
                    releaseVersionKickoffDate: null,
                    releaseVersionTargetKickoffDate: '2024-07-04T12:00:00-04:00',
                    isReleaseVersionTargetKickoffDatePredicted: true,
                    overridePilotId: null,
                    releaseType: ReleaseType.Minor,
                    pilotFromScheduleIntegrationEmail: 'narmstrong@demo.com',
                    isReleaseVersionTargetKickoffDatePredictedFromScheduleIntegration: false,
                    scheduleOnCallShiftStart: null,
                    scheduleOnCallShiftEnd: null,
                },
                {
                    id: 'ssullenberger@demo.com',
                    pilotRotationIndex: 3,
                    releaseVersion: '1.3.0',
                    isReleaseVersionPredicted: true,
                    releaseVersionKickoffDate: null,
                    releaseVersionTargetKickoffDate: '2024-07-11T12:00:00-04:00',
                    isReleaseVersionTargetKickoffDatePredicted: true,
                    overridePilotId: null,
                    releaseType: ReleaseType.Minor,
                    pilotFromScheduleIntegrationEmail: 'ssullenberger@demo.com',
                    isReleaseVersionTargetKickoffDatePredictedFromScheduleIntegration: false,
                    scheduleOnCallShiftStart: null,
                    scheduleOnCallShiftEnd: null,
                },
            ],
            syncInfo: null,
        },
        requiredReleaseStepTypesForApp: [
            ReleaseStepType.Kickoff,
            ReleaseStepType.Submission,
            ReleaseStepType.StoreReview,
            ReleaseStepType.Takeoff,
        ],
        nonBlockingReleaseStepTypes: [],
        releaseStepsBlockingConfigForReleaseType: {
            standard: {
                kickoff: ReleaseStepBlockingConfig.Blocking,
                featureReadiness: ReleaseStepBlockingConfig.Blocking,
                translations: ReleaseStepBlockingConfig.Blocking,
                releaseCandidate: ReleaseStepBlockingConfig.Blocking,
                regressionTesting: ReleaseStepBlockingConfig.Blocking,
                betaTesting: ReleaseStepBlockingConfig.Blocking,
                metadata: ReleaseStepBlockingConfig.Blocking,
                screenshots: ReleaseStepBlockingConfig.Blocking,
                approvals: ReleaseStepBlockingConfig.Blocking,
                submission: ReleaseStepBlockingConfig.Blocking,
                storeReview: ReleaseStepBlockingConfig.Blocking,
                takeoff: ReleaseStepBlockingConfig.Blocking,
                ciDistribution: ReleaseStepBlockingConfig.Blocking,
                vcsDistribution: ReleaseStepBlockingConfig.Blocking,
                rollbackBuild: ReleaseStepBlockingConfig.Blocking,
                rollout: ReleaseStepBlockingConfig.Blocking,
                releaseCycle: ReleaseStepBlockingConfig.Blocking,
            },
            hotfix: {
                kickoff: ReleaseStepBlockingConfig.Blocking,
                featureReadiness: ReleaseStepBlockingConfig.Blocking,
                translations: ReleaseStepBlockingConfig.Blocking,
                releaseCandidate: ReleaseStepBlockingConfig.Blocking,
                regressionTesting: ReleaseStepBlockingConfig.Blocking,
                betaTesting: ReleaseStepBlockingConfig.Blocking,
                metadata: ReleaseStepBlockingConfig.Blocking,
                screenshots: ReleaseStepBlockingConfig.Blocking,
                approvals: ReleaseStepBlockingConfig.Blocking,
                submission: ReleaseStepBlockingConfig.Blocking,
                storeReview: ReleaseStepBlockingConfig.Blocking,
                takeoff: ReleaseStepBlockingConfig.Blocking,
                ciDistribution: ReleaseStepBlockingConfig.Blocking,
                vcsDistribution: ReleaseStepBlockingConfig.Blocking,
                rollbackBuild: ReleaseStepBlockingConfig.Blocking,
                rollout: ReleaseStepBlockingConfig.Blocking,
                releaseCycle: ReleaseStepBlockingConfig.Blocking,
            },
            rollback: {
                kickoff: ReleaseStepBlockingConfig.Blocking,
                featureReadiness: ReleaseStepBlockingConfig.Blocking,
                releaseCandidate: ReleaseStepBlockingConfig.Blocking,
                regressionTesting: ReleaseStepBlockingConfig.Blocking,
                betaTesting: ReleaseStepBlockingConfig.Blocking,
                metadata: ReleaseStepBlockingConfig.Blocking,
                screenshots: ReleaseStepBlockingConfig.Blocking,
                approvals: ReleaseStepBlockingConfig.Blocking,
                submission: ReleaseStepBlockingConfig.Blocking,
                storeReview: ReleaseStepBlockingConfig.Blocking,
                takeoff: ReleaseStepBlockingConfig.Blocking,
                ciDistribution: ReleaseStepBlockingConfig.Blocking,
                vcsDistribution: ReleaseStepBlockingConfig.Blocking,
                rollbackBuild: ReleaseStepBlockingConfig.Blocking,
                translations: ReleaseStepBlockingConfig.Blocking,
                rollout: ReleaseStepBlockingConfig.Blocking,
                releaseCycle: ReleaseStepBlockingConfig.Blocking,
            },
        },
        shouldHideNonBlockingSteps: false,
        shouldHideFeatureFlagsSection: false,
        customMessagesConfig: {
            customMessageTemplatesData: {
                backMergePRTitle: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ],
                    defaultValue: 'Backmerge {releaseBranchName} to {workingBranchName}',
                },
                backMergePRBody: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue:
                        'Runway backmerged release branch `{releaseBranchName}` to working branch `{workingBranchName}`',
                },
                backMergeBranchPRBody: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                        '{targetBranchName}',
                    ] as PatternToken[],
                    defaultValue:
                        'Runway backmerged release branch `{releaseBranchName}` to working branch `{workingBranchName}`',
                },
                backMergeBranchPRTitle: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                        '{targetBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Backmerge {releaseBranchName} to {workingBranchName}',
                },
                backMergePRCommitPRBody: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                        '{commitShortHash}',
                        '{targetBranchName}',
                    ] as PatternToken[],
                    defaultValue:
                        'Runway backmerged release PR commit `{commitShortHash}` from `{releaseBranchName}` to branch `{targetBranchName}`',
                },
                backMergePRCommitPRTitle: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                        '{commitShortHash}',
                        '{targetBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Backmerge commit `{commitShortHash}` from {releaseBranchName} to {targetBranchName}',
                },
                bumpVersionCommitMessage: {
                    customStringType: '',
                    customStringValue: '[TAB-PREFIX] Bumping version to {version}',
                    maxCharCount: 72,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Bumping version to {version}',
                },
                bumpVersionPRBody: {
                    customStringType: '',
                    customStringValue: 'Runway bumped your version to {version} <Tab PR Body>',
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Runway bumped your version to {version}',
                },
                bumpVersionPRTitle: {
                    customStringType: '',
                    customStringValue: '[TAB-PREFIX] Bump version to {version}',
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Bump version to {version}',
                },
                cherryPickCommitMessage: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: ['{commitMessage}'] as PatternToken[],
                    defaultValue: 'Cherry-pick: {commitMessage}',
                },
                cherryPickPRBody: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: '',
                },
                cherryPickPRTitle: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: '',
                },
                promoteCodePRBody: {
                    customStringType: '',
                    customStringValue:
                        'Runway promoted `{workingBranchName}` to `{releaseBranchName}` for release {version} <Tab PR Body>',
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue:
                        'Runway promoted `{workingBranchName}` to `{releaseBranchName}` for release {version}',
                },
                promoteCodePRTitle: {
                    customStringType: '',
                    customStringValue: '[TAB-PREFIX] {workingBranchName} -> {releaseBranchName} for release {version}',
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: '{workingBranchName} -> {releaseBranchName} for release {version}',
                },
                tagMessage: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Release {version} tagged by Runway',
                },
                vcsReleaseBody: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: '',
                },
                vcsReleaseTitle: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 500,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                    ] as PatternToken[],
                    defaultValue: 'Release {version}',
                },
                releaseSummaryMessage: {
                    customStringType: '',
                    customStringValue: null,
                    maxCharCount: 1000,
                    availableTokens: [
                        '{version}',
                        '{versionConcise}',
                        '{releaseName}',
                        '{appStoreBuildNumber}',
                        '{ciBuildNumber}',
                        '{releasePilot}',
                        '{releaseBranchName}',
                        '{workingBranchName}',
                        '{workItems}',
                        '{appName}',
                        '{appPlatform}',
                        '{workItemsCount}',
                        '{issuesCount}',
                        '{commitsCount}',
                        '{contributorsCount}',
                        '{linesOfCodeCount}',
                        '{releaseNotes}',
                        '{releaseDuration}',
                        '{releasedAt}',
                        '{releaseWorkItemsLink}',
                        '{releaseAppStoreLink}',
                        '{releaseVCSReleaseLink}',
                    ] as PatternToken[],
                    defaultValue: 'Release {version}',
                },
            },
        },
        filterPaths: [],
        rolesMap: {
            pilot: {
                appStore: {
                    syncEnabled: false,
                    roles: [],
                },
            },
            approver: {
                appStore: {
                    syncEnabled: false,
                    roles: [],
                },
            },
            cx: {
                appStore: {
                    syncEnabled: true,
                    roles: [ASCRole.CustomerSupport],
                },
            },
            design: {
                appStore: {
                    syncEnabled: true,
                    roles: [ASCRole.Marketing],
                },
            },
            em: {
                appStore: {
                    syncEnabled: true,
                    roles: [ASCRole.AppManager],
                },
            },
            engineer: {
                appStore: {
                    syncEnabled: true,
                    roles: [ASCRole.Developer],
                },
            },
            marketing: {
                appStore: {
                    syncEnabled: false,
                    roles: [ASCRole.Marketing, ASCRole.Sales],
                },
            },
            ops: {
                appStore: {
                    syncEnabled: false,
                    roles: [ASCRole.CustomerSupport],
                },
            },
            pm: {
                appStore: {
                    syncEnabled: false,
                    roles: [ASCRole.AppManager],
                },
            },
            qa: {
                appStore: {
                    syncEnabled: false,
                    roles: [ASCRole.CustomerSupport],
                },
            },
        },
        stepGraph: {
            legacyAppId: '123456789',
            stepGroups: [],
        },
        enableExperimentalAIReleaseNotesFeature: false,
        storeEmailForwardingAddress: 'forwarding@runway.team',
        shouldDefaultToBuildDistro: false,
        hasCompletedQualitativeOnboardingAtLeastOnce: false,
        prescribedBetaSoakDurationSeconds: 86400,
        releaseBranchPatterns: [
            {
                id: '123456789',
                pattern: 'release-{version}',
                isHotfix: false,
                isDefault: true,
                isStatic: false,
            },
        ],
        releaseTagPatterns: [{ id: '123-456', pattern: 'v{version}' }],
        featureAffiliations: [
            {
                id: 'abcdefg',
                pattern: '{version}',
                integrationId: 'jira',
                affiliationType: 'jira-fix-version',
            },
        ],
        integrations: [
            {
                id: 'amplitude',
                installationId: 'amplitude:12345',
                name: 'Amplitude',
                providerId: 'amplitude:abcdefg',
                integrationError: null,
                capabilities: ['productAnalytics'],
                data: {
                    selectedEventsAndParams: [
                        { eventName: 'checkout success' },
                        { eventName: 'add to cart' },
                        { eventName: 'save favorite' },
                        { eventName: 'view favorites' },
                        { eventName: 'view product page' },
                        { eventName: 'invite friend' },
                    ],
                    supportedMetricTypes: [
                        'eventsPerActiveUser',
                        'eventsPerActiveUserDeltaFromPreviousPercentage',
                        'eventsPerActiveUserDeltaFromPreviousValue',
                        'eventsPerSession',
                        'eventsPerSessionDeltaFromPreviousPercentage',
                        'eventsPerSessionDeltaFromPreviousValue',
                        'avgPropertyValue',
                        'avgPropertyValueDeltaFromPreviousPercentage',
                        'avgPropertyValueDeltaFromPreviousValue',
                        'medianPropertyValue',
                        'medianPropertyValueDeltaFromPreviousPercentage',
                        'medianPropertyValueDeltaFromPreviousValue',
                        'p90PropertyValue',
                        'p90PropertyValueDeltaFromPreviousPercentage',
                        'p90PropertyValueDeltaFromPreviousValue',
                        'p95PropertyValue',
                        'p95PropertyValueDeltaFromPreviousPercentage',
                        'p95PropertyValueDeltaFromPreviousValue',
                        'p99PropertyValue',
                        'p99PropertyValueDeltaFromPreviousPercentage',
                        'p99PropertyValueDeltaFromPreviousValue',
                        'genericObaaValue',
                        'genericObaaValueDeltaFromPreviousPercentage',
                        'genericObaaValueDeltaFromPreviousValue',
                    ],
                },
                refreshData: [
                    {
                        lastRefreshedAt: '2022-11-03T01:27:26.141285449Z',
                        nextRefreshAt: '2022-11-03T01:52:26.141282512Z',
                        integrationId: 'amplitude',
                    },
                ],
            },
            {
                id: 'apple',
                installationId: 'apple:12345',
                name: 'App Store',
                providerId: 'apple:12345',
                integrationError: null,
                capabilities: ['appStore'],
                refreshData: [
                    {
                        lastRefreshedAt: '2022-11-03T01:27:26.141285449Z',
                        nextRefreshAt: '2022-11-03T01:52:26.141282512Z',
                        integrationId: 'apple',
                    },
                ],
                data: { appId: 12345678, bundleId: 'com.demo.demo' },
            },
            {
                id: 'apple-beta',
                installationId: 'apple-beta:12345',
                name: 'TestFlight',
                providerId: 'apple:12345',
                integrationError: null,
                capabilities: ['betaTesting'],
                data: {
                    appId: 12345678,
                    bundleId: 'com.demo.demo',
                    consoleUrl: 'https://appstoreconnect.apple.com/',
                },
                refreshData: [
                    {
                        lastRefreshedAt: '2025-01-23T08:01:19.298158407Z',
                        nextRefreshAt: '2025-01-23T08:09:19.298158407Z',
                        integrationId: 'apple-beta',
                    },
                ],
            },
            {
                id: 'bitrise',
                installationId: 'bitrise:12345',
                name: 'Bitrise',
                providerId: 'bitrise:12345',
                integrationError: null,
                capabilities: ['buildSystem'],
                data: {
                    allowMatchingMultipleAppStoreBuilds: null,
                    appSlug: 'demo-ios',
                    appTitle: 'demo-ios',
                    devWorkflowId: 'build',
                    devWorkflowName: 'build',
                    prodWorkflowId: null,
                    prodWorkflowName: null,
                    workflowId: 'deploy',
                    workflowName: 'deploy',
                },
                refreshData: [
                    {
                        integrationId: 'bitrise',
                        lastRefreshedAt: '2023-06-22T13:20:05.723157338Z',
                        nextRefreshAt: '2023-06-22T13:25:05.723157338Z',
                    },
                ],
            },
            {
                id: 'github',
                installationId: 'github:12345',
                name: 'GitHub',
                providerId: 'github:12345',
                integrationError: null,
                capabilities: ['versionControl'],
                data: { repoName: appId, repoOwner: appName.toLocaleLowerCase() },
                refreshData: [
                    {
                        integrationId: 'github',
                        lastRefreshedAt: '2023-06-22T13:10:19.817275544Z',
                        nextRefreshAt: '2023-06-22T13:29:20.642971228Z',
                    },
                ],
            },
            {
                id: 'jira',
                installationId: 'jira:12345',
                name: 'Jira',
                providerId: 'jira:12345',
                integrationError: null,
                capabilities: ['featureManagement'],
                data: {
                    additionalDoneStatuses: [],
                    selectedProjects: [
                        { id: '10000', key: 'DEMO', name: 'Demo' },
                        { id: '10001', key: 'DEMO-CORE', name: 'Demo-Core' },
                    ],
                    customFields: [],
                    autoSelectAllProjects: false,
                },
                refreshData: [
                    {
                        integrationId: 'jira',
                        lastRefreshedAt: '2023-06-22T13:20:00.490582638Z',
                        nextRefreshAt: '2023-06-22T13:26:00.490582638Z',
                    },
                ],
            },
            {
                id: 'sentry',
                installationId: 'sentry:12345',
                name: 'Sentry',
                providerId: 'sentry:12345',
                integrationError: null,
                capabilities: ['stabilityMonitoring'],
                data: {
                    betaEnvironment: 'development',
                    prodEnvironment: 'production',
                    selectedProject: {
                        displayableName: 'demo-ios',
                        environments: ['development', 'production'],
                        id: 'abc',
                        orgId: '12345',
                    },
                    supportedMetricTypes: [
                        'userStability',
                        'userStabilityDeltaFromPreviousPercentage',
                        'userStabilityDeltaFromPreviousValue',
                        'sessionStability',
                        'sessionStabilityDeltaFromPreviousPercentage',
                        'sessionStabilityDeltaFromPreviousValue',
                    ],
                },
                refreshData: [
                    {
                        integrationId: 'sentry',
                        refreshType: 'StabilityInfo',
                        lastRefreshedAt: '2023-06-22T13:15:13.10131537Z',
                        nextRefreshAt: '2023-06-22T13:25:13.10131537Z',
                    },
                    {
                        integrationId: 'sentry',
                        refreshType: 'StabilityIssues',
                        lastRefreshedAt: '2023-06-22T13:17:00.10131537Z',
                        nextRefreshAt: '2023-06-22T13:32:00.10131537Z',
                    },
                ],
            },
            {
                id: 'slack',
                installationId: 'slack:12345',
                name: 'Slack',
                providerId: 'slack:abcdefg',
                integrationError: null,
                capabilities: ['notifications'],
                data: {
                    primaryChannels: [
                        {
                            id: 'ABC',
                            name: '#general',
                            isRedacted: false,
                            displayName: '#general',
                        },
                    ],
                    shouldAppendNotificationSettings: true,
                },
                refreshData: [],
            },
            {
                id: 'testrail',
                installationId: 'testrail:12345',
                name: 'TestRail',
                providerId: 'testrail:12345',
                integrationError: null,
                capabilities: ['regressionTesting'],
                data: {
                    selectedProject: { id: '1', name: 'Demo Testrail iOS', isSingleSuite: true },
                    testRunNameKeywords: ['iOS'],
                    testRunNameKeywordsMatchAny: false,
                },
                refreshData: [],
            },
            {
                id: 'optimizely',
                installationId: 'optimizely:12345',
                name: 'Optimizely',
                providerId: 'optimizely:abcdefg',
                integrationError: null,
                capabilities: ['featureFlagging'],
                data: {
                    selectedEnvironment: {
                        id: 'production',
                        name: 'Production',
                    },
                    selectedProject: {
                        id: '123456',
                        isClassic: false,
                        isUsingFlagsAPI: true,
                        name: isIosPlatform(platform) ? 'iOS' : 'Android',
                    },
                    selectedVersionAttributeName: 'app_version',
                },
                refreshData: [
                    {
                        lastRefreshedAt: '2022-12-23T02:26:25.429216967Z',
                        nextRefreshAt: '2022-12-23T02:36:25.429216967Z',
                        integrationId: 'optimizely',
                    },
                ],
            },
            {
                id: 'crowdin',
                installationId: 'crowdin:12345',
                name: 'Crowdin',
                providerId: 'crowdin:abcdefg',
                integrationError: null,
                capabilities: ['translations'],
                data: {
                    selectedProject: {
                        id: '622958',
                        key: appId,
                        name: appName,
                        identifier: appName.toLocaleLowerCase(),
                        url: `https://crowdin.com/project/${appId}`,
                    },
                    selectedMetadataProject: {
                        id: '622959',
                        key: `${appId}-metadata`,
                        name: `${appName} Metadata`,
                        identifier: `${appName.toLocaleLowerCase()}-metadata`,
                        url: `https://crowdin.com/project/${appId}-metadata`,
                    },
                },
                refreshData: [
                    {
                        lastRefreshedAt: '2024-02-26T10:32:24.099803375Z',
                        nextRefreshAt: '2024-02-26T10:42:24.099803375Z',
                        integrationId: 'crowdin',
                    },
                ],
            },
            {
                id: 'pagerduty-scheduling',
                installationId: 'pagerduty:12345',
                name: 'PagerDuty',
                providerId: 'pagerduty:abcdefg',
                integrationError: null,
                capabilities: ['scheduling'],
                data: {
                    selectedSchedule: {
                        id: '123456',
                        name: `${appName} Release Rotation`,
                    },
                },
                refreshData: [],
            },
            {
                id: 'google-calendar',
                installationId: 'google:678910',
                name: 'Google Calendar',
                providerId: 'google:hijklmnop',
                integrationError: null,
                capabilities: ['calendar'],
                data: {
                    calendarId: `${appName.toLocaleLowerCase()}-cal`,
                    appDisplayName: appName,
                    calendarName: `${appName} Calendar`,
                },
                refreshData: [],
            },
        ],
        stepChecklistItems: [
            {
                id: 'bvv0q9kfoo7cohhp7jog',
                title: 'Verify production feature flags are correct for this release',
                description: null,
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'featureReadiness',
                nextItemId: 'c89r7hdn86bogie862eg',
                approverGroups: ['engineer'],
                releaseTypes: 'all',
                notifyEnabled: true,
                oneOffForVersion: null,
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                owners: [],
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
            {
                id: 'bvv18nkfoo7cohhp7ju0',
                title: 'Send announcement email to team@ distribution list',
                description: null,
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'takeoff',
                nextItemId: 'bvv1agcfoo7cohhp7jug',
                approverGroups: ['pilot'],
                releaseTypes: 'all',
                notifyEnabled: true,
                oneOffForVersion: null,
                owners: [],
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
            {
                id: 'c5piqj5n86bot448ibqg',
                title: 'Send out reminder for upcoming release deadlines',
                description: 'Some detail',
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'kickoff',
                nextItemId: 'c7nlrbtn86burlh1hd50',
                approverGroups: ['pilot'],
                releaseTypes: 'all',
                notifyEnabled: true,
                oneOffForVersion: null,
                owners: [],
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
            {
                id: 'c7lhj6j2dnc33t344q30',
                title: 'Verify versioning with product team',
                description: null,
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'kickoff',
                nextItemId: 'c8tqlqtn86bs9kuefhjg',
                approverGroups: [],
                releaseTypes: 'all',
                notifyEnabled: true,
                oneOffForVersion: null,
                owners: [],
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
            {
                id: 'c8b6205n86bupefccp1g',
                title: 'Verify that all critical bugfixes slated for the release have been merged.',
                description:
                    "Any critical/show-stopper bugfixes will be tagged with the 'Critical' label. All of those must be in the release before submitting.",
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'featureReadiness',
                nextItemId: null,
                approverGroups: ['pilot'],
                releaseTypes: 'all',
                notifyEnabled: true,
                oneOffForVersion: null,
                owners: [],
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
            {
                id: 'c91jkekjehqmb4ijenh0',
                title: 'Verify that any stability issues uncovered during beta testing have been ticketed and triaged.',
                description: '',
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'betaTesting',
                nextItemId: null,
                approverGroups: ['qa'],
                releaseTypes: 'all',
                notifyEnabled: false,
                oneOffForVersion: null,
                owners: [],
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
            {
                id: 'c97ehltn86bt4olk85ag',
                title: 'Collect all beta testing feedback and catalog in backlog for appropriate teams.',
                description: '',
                type: 'binary',
                placement: 'stepChecklist',
                statuses: ['approved'],
                releaseStepType: 'betaTesting',
                nextItemId: 'c91jkekjehqmb4ijenh0',
                approverGroups: ['cx'],
                releaseTypes: 'all',
                notifyEnabled: true,
                oneOffForVersion: null,
                owners: [],
                uid: 'bvv0q9kfoo7cohhp7jog',
                createdAt: '2021-06-22T13:20:05.723157338Z',
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                orgTemplateId: '',
            },
        ],
        groups: {
            approver: [],
            cx: [],
            design: [],
            em: [],
            engineer: ['ssullenberger@demo.com', 'aearhart@demo.com', 'narmstrong@demo.com'],
            marketing: [],
            ops: [],
            pilot: ['ssullenberger@demo.com', 'aearhart@demo.com', 'narmstrong@demo.com'],
            pm: [],
            qa: [],
        },
        workingBranch: 'development',
        stagingBranch: null,
        prodBranch: null,
        usesStaticReleaseBranchForNonHotfixes: false,
        isPureTrunkBasedBranchingForNonHotfixes: false,
        appStoreUrl: 'https://apps.apple.com/us/app/123456',
        allReleaseStepTypesForApp: [
            'kickoff',
            'featureReadiness',
            'translations',
            'releaseCandidate',
            'regressionTesting',
            'betaTesting',
            'metadata',
            'screenshots',
            'approvals',
            'submission',
            'storeReview',
            'takeoff',
        ],
        appStoreDefaults: {
            exportComplianceInfoUsesEncryption: false,
            phasedRelease: true,
            releaseMethod: 'releaseMethodManual',
            resetSummaryRating: false,
        },
        automations: [],
        appStoreDefaultReleaseNotes: [
            {
                id: 'tab:en-US',
                locale: 'en-US',
                displayLocale: 'American English',
                whatsNew: 'Minor bugfixes and improvements!',
            },
            {
                id: 'tab:es-ES',
                locale: 'es-ES',
                displayLocale: 'European Spanish',
                whatsNew: 'Correcciónes de errores y mejoras menores!',
            },
        ],
        releaseScheduleCadenceSettings: {
            scheduleCadence: 'weekly',
            cadenceTargetDateSets: [
                {
                    kickoffDayOfWeek: 'tuesday',
                    kickoffTimeHour: 13,
                    submitDayOfWeek: 'thursday',
                    submitTimeHour: 14,
                    releaseDayOfWeek: 'saturday',
                    releaseTimeHour: 17,
                },
            ],
            cadenceTimezone: 'America/New_York',
            allowKickoffWhenPreviousNotComplete: false,
        },
        defaultReleaseType: ReleaseType.Minor,
        releasePilotIds: ['ssullenberger@demo.com', 'aearhart@demo.com', 'narmstrong@demo.com'],
        notifications: [
            {
                id: 'STEP_APPROVAL',
                displayText: 'A step was approved',
                info: '​One of your release steps was marked as approved (for steps that need to be manually approved, currently applies to Screenshots and Metadata).',
                enabled: true,
                channels: [],
            },
            {
                id: 'APP_STORE_STATUS_UPDATE',
                displayText: 'App store status was updated',
                info: 'Your status in the app store was changed.',
                enabled: true,
                channels: [],
            },
            {
                id: 'CHECKLIST_ITEM_COMMENT',
                displayText: 'Approvals/regression item comment added',
                info: 'A comment was added to a checklist item.',
                enabled: true,
                channels: [],
            },
            {
                id: 'AUTO_RELEASE_FAILED',
                displayText: 'Automatic release by Runway failed',
                info: 'Runway was unable to automatically release your app update at the target time/date.',
                enabled: true,
                channels: [],
            },
            {
                id: 'AUTO_RELEASE_FAILED_INCOMPLETE_STEPS',
                displayText: 'Automatic release by Runway failed - incomplete steps',
                info: 'Runway couldn’t automatically release your app update at the target time/date because one or more required steps were not complete (green).',
                enabled: true,
                channels: [],
            },
            {
                id: 'AUTO_RELEASE_TO_APP_STORE',
                displayText: 'Automatic release by Runway was successful',
                info: 'Runway automatically released (or started a phased release of) your app update at the target time/date.',
                enabled: true,
                channels: [],
            },
            {
                id: 'AUTO_SUBMIT_FAILED',
                displayText: 'Automatic submit failed',
                info: 'Runway was unable to automatically submit your release for review at the target time/date.',
                enabled: true,
                channels: [],
            },
            {
                id: 'AUTO_SUBMIT_FAILED_INCOMPLETE_STEPS',
                displayText: 'Automatic submit failed - incomplete steps',
                info: 'Runway couldn’t automatically submit your release for review at the target time/date because one or more required steps were not complete (green).',
                enabled: true,
                channels: [],
            },
            {
                id: 'AUTO_SUBMIT_TO_APP_STORE',
                displayText: 'Automatic submit for review by Runway was successful',
                info: 'Runway automatically submitted your app update to Apple or Google for review.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_SOAK_END',
                displayText: 'Beta soak complete',
                info: 'Beta soak period has fully elapsed.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_SOAK_MANUAL_END',
                displayText: 'Beta soak ended manually',
                info: 'Beta soak period was marked as complete.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_SOAK_START',
                displayText: 'Beta soak started',
                info: 'A new beta soak period started due to a new CI build becoming available.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_READY_FOR_EXTERNAL_TESTING',
                displayText: 'Beta testing build available for external testing',
                info: 'A build is now available for external testing on TestFlight (iOS).',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_READY_FOR_INTERNAL_TESTING',
                displayText: 'Beta testing build available for internal testing',
                info: 'A new build is now available for internal testers on TestFlight (iOS), or has been promoted to a Google Play testing track (Android).',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_SUBMITTED_FOR_REVIEW',
                displayText: 'Beta testing build submitted for review for external testing',
                info: 'A beta testing build in TestFlight (iOS) was submitted for review in order to be available to external testers.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BUILD_FAILED',
                displayText: 'Build failed',
                info: 'CI workflow for a new CI build failed.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BUILD_STOPPED',
                displayText: 'Build stopped',
                info: '​CI workflow for a new CI build was stopped.',
                enabled: true,
                channels: [],
            },
            {
                id: 'CHECKLIST_ITEM_APPROVED',
                displayText: 'Checklist/regression/approval item completed',
                info: 'One of your checklist/regression/approval items was marked as done.',
                enabled: true,
                channels: [],
            },
            {
                id: 'CHECKLIST_ITEM_INCOMPLETE',
                displayText: 'Checklist item incomplete',
                info: 'One or more checklist items is incomplete and is preventing a step from being considered done (green).',
                enabled: true,
                channels: [],
            },
            {
                id: 'RELEASE_AUTOTAG_FAIL',
                displayText: "Couldn't tag release",
                info: 'Runway tried and failed to automatically tag your completed release.',
                enabled: true,
                channels: [],
            },
            {
                id: 'DAILY_USER_REVIEWS_DIGEST',
                displayText: 'Daily user reviews digest',
                info: 'A recap of new user reviews of your app within the past day.',
                enabled: true,
                channels: [],
            },
            {
                id: 'FR_READY',
                displayText: 'Feature Readiness step is complete',
                info: 'All work slated for this release (determined by tickets or code affiliated with the current version) is done.',
                enabled: true,
                channels: [],
            },
            {
                id: 'FA_APPLIED',
                displayText: 'Feature affiliations applied',
                info: 'Runway automatically applied missing labels and/or Fix versions to tickets associated with your release in your project management tool.',
                enabled: true,
                channels: [],
            },
            {
                id: 'RELEASE_KICKOFF_FAILED',
                displayText: 'Kickoff failed',
                info: 'Your release failed to kick off.',
                enabled: true,
                channels: [],
            },
            {
                id: 'TARGET_KICKOFF_REMINDER',
                displayText: 'Kickoff reminder before target time/date',
                info: 'Your target release kickoff time/date is approaching.',
                enabled: true,
                channels: [],
            },
            {
                id: 'RELEASE_KICKOFF',
                displayText: 'Kickoff successful',
                info: 'Your release kicked off successfully.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BUMP_VERSION_PR_MERGED',
                displayText: 'Bump version PR merged',
                info: 'Runway has merged a bump version PR.',
                enabled: true,
                channels: [],
            },
            {
                id: 'MANUAL_RELEASE_TO_APP_STORE',
                displayText: 'Manual app store release successful',
                info: 'Your app update was manually released to the app store.',
                enabled: true,
                channels: [],
            },
            {
                id: 'MANUAL_SUBMIT_TO_APP_STORE',
                displayText: 'Manual submit to app store successful',
                info: 'Your app update was manually submitted to the app store for review.',
                enabled: true,
                channels: [],
            },
            {
                id: 'METADATA_APPLIED',
                displayText: 'Metadata defaults were applied',
                info: 'Runway automatically applied your default metadata to this release.',
                enabled: true,
                channels: [],
            },
            {
                id: 'METADATA_EDITABLE',
                displayText: 'Metadata is editable',
                info: 'Metadata can now be edited for this release.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BETA_BUILD_AVAIL',
                displayText: 'New beta build available',
                info: 'A new beta build is available in TestFlight or Play Console.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BUILD_AVAIL',
                displayText: 'New build available',
                info: 'CI successfully completed a new CI build.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BUILD_AVAIL_ARTIFACTS',
                displayText: 'New build available with artifacts',
                info: 'CI successfully completed a new CI build, and build artifacts are available to download.',
                enabled: true,
                channels: [],
            },
            {
                id: 'BUILD_STARTED',
                displayText: 'New build in progress',
                info: '​CI started a workflow for a new CI build.',
                enabled: true,
                channels: [],
            },
            {
                id: 'PRODUCTION_WORKFLOW_SUCCEEDED',
                displayText: 'New production workflow build available',
                info: 'CI successfully completed a new production build (for teams with a separate production build workflow).',
                enabled: true,
                channels: [],
            },
            {
                id: 'INDIVIDUAL_REVIEW',
                displayText: 'New user review',
                info: 'A new user review of your app was published.',
                enabled: true,
                channels: [],
            },
            {
                id: 'FR_NOT_READY',
                displayText: 'New work has been added to this release',
                info: 'A ticket or code was detected that prevented the Feature Readiness step from being considered done (green).',
                enabled: true,
                channels: [],
            },
            {
                id: 'APP_STORE_PHASED_RELEASE_UPDATE',
                displayText: 'Phased release / Staged rollout progress update',
                info: 'An update on the progress of your phased release (iOS) / staged rollout (Android).',
                enabled: true,
                channels: [],
            },
            {
                id: 'APP_STORE_PHASED_RELEASE_COMPLETE',
                displayText: 'Phased release complete',
                info: 'Your phased release has finished rolling out to 100% of users.',
                enabled: true,
                channels: [],
            },
            {
                id: 'NEXT_UP_PILOT_REMINDER',
                displayText: 'Pilot reminder for upcoming release',
                info: 'A reminder for the pilot of the next release. Sent after an app update is successfully released, or when an existing release pilot is changed.',
                enabled: true,
                channels: [],
            },
            {
                id: 'REG_STATUS_UPDATE',
                displayText: 'Regression testing status updated',
                info: 'The status of your regression testing was changed to: started, not started, passed, or failed.',
                enabled: true,
                channels: [],
            },
            {
                id: 'RELEASE_ANNOUNCEMENT',
                displayText: 'Release announcement',
                info: 'A detailed message about your newly-completed release.',
                enabled: true,
                channels: [],
            },
            {
                id: 'TARGET_RELEASE_REMINDER',
                displayText: 'Release reminder before target time/date',
                info: 'Your target app release time/date is approaching. This notification will also call out any remaining incomplete steps.',
                enabled: true,
                channels: [],
            },
            {
                id: 'RELEASE_TAGGED',
                displayText: 'Release tagged',
                info: 'Your completed release was tagged by Runway.',
                enabled: true,
                channels: [],
            },
            {
                id: 'PAUSE_RESUME_SCHEDULE_CADENCE',
                displayText: 'Schedule cadence paused or resumed',
                info: 'Your schedule of recurring target times/dates in Runway was paused or resumed.',
                enabled: true,
                channels: [],
            },
            {
                id: 'SET_SELECTED_BUILD_APP_STORE',
                displayText: 'Selected build in app store was changed',
                info: 'The selected build for release in the app store was manually changed via Runway.',
                enabled: true,
                channels: [],
            },
            {
                id: 'STABLE_PHASED_RELEASE_PROMOTED',
                displayText: 'Stable phased release accelerated to 100%',
                info: 'Runway automatically accelerated your phased release / staged rollout to 100% of users due to a high session stability rate.',
                enabled: true,
                channels: [],
            },
            {
                id: 'TARGET_SUBMIT_REMINDER',
                displayText: 'Submit reminder before target time/date',
                info: 'Your target app submission time/date is approaching. This notification will also call out any remaining incomplete steps.',
                enabled: true,
                channels: [],
            },
            {
                id: 'UNSTABLE_PHASED_RELEASE_HALTED',
                displayText: 'Unstable phased release halted',
                info: 'Runway automatically paused your phased release due to a low session stability rate.',
                enabled: true,
                channels: [],
            },
            {
                id: 'GOOGLE_CUSTOM_STAGED_ROLLOUT_MANUALLY_STARTED',
                displayText: 'Google custom staged rollout manually started',
                info: 'A custom staged rollout was manually started in Google Play Console.',
                enabled: true,
                channels: [],
            },
            {
                id: 'GOOGLE_CUSTOM_STAGED_ROLLOUT_FAILED_TO_START',
                displayText: 'Google custom staged rollout failed to start',
                info: 'Google custom staged rollout failed to start because of one or more incomplete steps.',
                enabled: true,
                channels: [],
            },
        ],
        bumpVersionFiles: [
            {
                fileName: `${appName.toLocaleLowerCase()}.xcodeproj/project.pbxproj`,
                currentVersion: '1.1.0',
                branch: 'development',
            },
            {
                fileName: `${appName.toLocaleLowerCase()}/settings.xcconfig`,
                currentVersion: '1.1.0',
                branch: 'development',
            },
        ],
        translationDirectories: [
            {
                name: `${appName.toLocaleLowerCase()}/resources`,
            },
        ],
        monitoringSettings: {
            metrics: [
                {
                    category: 'userReviews',
                    thresholds: [
                        {
                            value: 3,
                            condition: 'GreaterThan',
                        },
                    ],
                    type: 'avgUserRating',
                    id: 'userReviews@avgUserRating',
                    integrationId: 'apple',
                    hideOnRolloutView: false,
                    integrationInstallationIds: ['apple:xid'],
                    notifyOnApproachingThreshold: false,
                },
                {
                    thresholds: [
                        {
                            value: 0.1,
                            condition: 'GreaterThan',
                        },
                    ],
                    category: 'productAnalytics',
                    type: 'eventsPerActiveUser',
                    id: 'productAnalytics@eventsPerActiveUser:checkout success',
                    integrationId: 'amplitude',
                    eventName: 'Checkout success',
                    hideOnRolloutView: false,
                    integrationInstallationIds: ['amplitude:xid'],
                    notifyOnApproachingThreshold: false,
                },
                {
                    thresholds: [
                        {
                            value: 0.1,
                            condition: 'GreaterThan',
                        },
                    ],
                    category: 'productAnalytics',
                    type: 'eventsPerSession',
                    id: 'productAnalytics@eventsPerSession:checkout success',
                    integrationId: 'amplitude',
                    eventName: 'Checkout success',
                    hideOnRolloutView: false,
                    integrationInstallationIds: ['amplitude:xid'],
                    notifyOnApproachingThreshold: false,
                },
                {
                    thresholds: [
                        {
                            value: 95.95,
                            condition: 'GreaterThan',
                        },
                    ],
                    category: 'stability',
                    type: 'sessionStability',
                    id: 'stability@sessionStability',
                    integrationId: 'sentry',
                    hideOnRolloutView: false,
                    integrationInstallationIds: ['sentry:xid'],
                    notifyOnApproachingThreshold: false,
                },
            ],
            checklistItems: [],
        },
        addFixRequestsToOpenPRsAgainstReleaseBranches: false,
        requiresFixRequestsApprovals: true,
        fixRequestFormFieldsSettings: demoFixRequestFormFieldsSettings,
        enableStatusChecksForFixRequests: false,
        use24HourTimeFormat: false,
        shouldShowBetaOverlayOnWorkflowsBetaOrgs: false,
        mergeUserCreatedPRs: false,
        workingBranchUserCreatedPRMergeType: MergeType.DoNotMerge,
        releaseBranchUserCreatedPRMergeType: MergeType.DoNotMerge,
        bundleSizeDeviceModelId: '',
        ascDeviceModels: [],
    };
};

const mockWorkflowsAppSettings = (
    appId: string,
    appName: string,
    appImageUrl: string,
    destinations: Destination[],
    stepGraph: WorkflowStepGraph,
): AppSettings => {
    const baseSettings = legacyAppSettingsMock(appId, appName, AppPlatform.Ios) as unknown as AppSettings;
    const integrationOverrides = flatMap(destinations, (dest) => {
        if (dest.platform === AppPlatform.Android) {
            return androidIntegrations;
        }

        if (dest.platform === AppPlatform.Ios) {
            return iosIntegrations;
        }

        return [];
    }) as Integration[];

    // Generate automations per destination, but de-duplicate lifecycle automations
    const allAutomations = flatMap(destinations, (dest) => {
        const workflowAutomations = workflowOnlyAutomations(dest.platform, dest.appStoreType);

        if (dest.platform === AppPlatform.Android) {
            return [...workflowAutomations, ...androidAutomations];
        }

        if (dest.platform === AppPlatform.Ios) {
            return [...workflowAutomations, ...iosAutomations];
        }

        return workflowAutomations;
    });

    // De-duplicate lifecycle automations (those without stepConfigId) by type
    // Keep all step-based automations (those with stepConfigId) since they're per-destination
    const lifecycleAutomations = unionBy(
        allAutomations.filter((a) => !a!.stepConfigId),
        (automation) => automation!.type,
    );
    const stepBasedAutomations = allAutomations.filter((a) => a!.stepConfigId);
    const automations = [...lifecycleAutomations, ...stepBasedAutomations] as Automation[];

    return {
        ...baseSettings,
        appImageUrl,
        destinations,
        isWorkflowsMigratedAndEnabled: true,
        shouldShowBetaOverlayOnWorkflowsBetaOrgs: true,
        stepGraph: stepGraph,
        workflowEnabledSteps: Object.values(ReleaseStepType).reduce((acc, type) => {
            // Kickoff and FeatureReadiness are not migrated to workflows yet
            if (type !== ReleaseStepType.Kickoff && type !== ReleaseStepType.FeatureReadiness) {
                acc[type] = true;
            }
            return acc;
        }, {}),
        destinationInfoByDestinationStepConfigId: destinations.reduce((acc, destination) => {
            acc[destination.stepConfigId] = destination;
            return acc;
        }, {}),
        branchConfigsById: {},
        tagConfigsById: {},
        featureAffiliationConfigsById: {},
        automations,
        integrations: unionBy([...baseSettings.integrations, ...integrationOverrides], (integration) => integration.id),
    };
};

export const mockCrossPlatformAppSettings = (appId: string): AppSettings => {
    const destinations = [DEMO_DESTINATIONS.geminiIos, DEMO_DESTINATIONS.geminiAndroid];
    const allDestinationIds = destinations.map((d) => d.stepConfigId);

    return mockWorkflowsAppSettings(
        appId,
        'Gemini',
        'https://runway-img.s3.us-east-2.amazonaws.com/app-demo-gemini.png',
        destinations,
        {
            legacyAppId: '123',
            stepGroups: [
                {
                    archetypeGroups: [
                        {
                            name: 'Kickoff',
                            archetype: ReleaseStepType.Kickoff,
                            steps: [
                                {
                                    stepConfigId: 'kickoff',
                                    stepName: 'Kickoff',
                                    archetype: ReleaseStepType.Kickoff,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: ['github:12345', 'slack:12345'],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                        {
                            name: 'Feature readiness',
                            archetype: ReleaseStepType.FeatureReadiness,
                            steps: [
                                {
                                    stepConfigId: 'feature-readiness',
                                    stepName: 'Feature readiness',
                                    archetype: ReleaseStepType.FeatureReadiness,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: ['jira:12345', 'github:12345'],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'CI',
                            archetype: ReleaseStepType.ReleaseCandidate,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `ci-${dest.stepConfigId}`,
                                stepName: `CI ${dest.name}`,
                                archetype: ReleaseStepType.ReleaseCandidate,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['bitrise:12345', 'github:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Beta testing',
                            archetype: ReleaseStepType.BetaTesting,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `beta-${dest.stepConfigId}`,
                                stepName: `Beta ${dest.name}`,
                                archetype: ReleaseStepType.BetaTesting,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ? ['apple-beta:12345'] : ['google-beta:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Regression testing',
                            archetype: ReleaseStepType.RegressionTesting,
                            steps: [
                                {
                                    stepConfigId: 'regression-testing',
                                    stepName: 'Regression testing',
                                    archetype: ReleaseStepType.RegressionTesting,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: ['testrail:12345'],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Screenshots',
                            archetype: ReleaseStepType.Screenshots,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `screenshots-${dest.stepConfigId}`,
                                stepName: `Screenshots ${dest.name}`,
                                archetype: ReleaseStepType.Screenshots,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ? ['apple:12345'] : ['google:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Metadata',
                            archetype: ReleaseStepType.Metadata,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `metadata-${dest.stepConfigId}`,
                                stepName: `Metadata ${dest.name}`,
                                archetype: ReleaseStepType.Metadata,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ?
                                        ['apple:12345', 'crowdin:12345']
                                    :   ['google:12345', 'crowdin:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Submission approvals',
                            archetype: ReleaseStepType.Approvals,
                            steps: [
                                {
                                    stepConfigId: 'submission-approvals',
                                    stepName: 'Submission approvals',
                                    archetype: ReleaseStepType.Approvals,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: [],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Submission',
                            archetype: ReleaseStepType.Submission,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `submission-${dest.stepConfigId}`,
                                stepName: `Submission ${dest.name}`,
                                archetype: ReleaseStepType.Submission,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ? ['apple:12345'] : ['google:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Review',
                            archetype: ReleaseStepType.StoreReview,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `review-${dest.stepConfigId}`,
                                stepName: `Review ${dest.name}`,
                                archetype: ReleaseStepType.StoreReview,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ? ['apple:12345'] : ['google:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Release approvals',
                            archetype: ReleaseStepType.Approvals,
                            steps: [
                                {
                                    stepConfigId: 'release-approvals',
                                    stepName: 'Release approvals',
                                    archetype: ReleaseStepType.Approvals,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: [],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Release',
                            archetype: ReleaseStepType.Takeoff,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `release-${dest.stepConfigId}`,
                                stepName: `Release ${dest.name}`,
                                archetype: ReleaseStepType.Takeoff,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ? ['apple:12345'] : ['google:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Rollout',
                            archetype: ReleaseStepType.Rollout,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `rollout-${dest.stepConfigId}`,
                                stepName: `Rollout ${dest.name}`,
                                archetype: ReleaseStepType.Rollout,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds:
                                    dest.platform === AppPlatform.Ios ?
                                        ['apple:12345', 'sentry:12345', 'amplitude:12345']
                                    :   ['google:12345', 'sentry:12345', 'amplitude:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                    ],
                },
            ],
        },
    );
};

export const mockVoyagerAppSettings = (appId: string): AppSettings => {
    const destinations = [
        DEMO_DESTINATIONS.voyagerIos,
        DEMO_DESTINATIONS.voyagerAndroid,
        DEMO_DESTINATIONS.voyagerExpo,
    ];
    const nativeDestinations = destinations.filter((destination) => destination.platform !== AppPlatform.ExpoEAS);
    const allDestinationIds = destinations.map((destination) => destination.stepConfigId);
    const platformName = (destination: Destination) =>
        destination.platform === AppPlatform.ExpoEAS ? 'Expo'
        : destination.platform === AppPlatform.Ios ? 'iOS'
        : 'Android';
    const platformIntegrationIds = (destination: Destination) =>
        destination.platform === AppPlatform.Ios ? ['apple:12345']
        : destination.platform === AppPlatform.Android ? ['google:12345']
        : ['expo-eas:12345'];
    const destinationSteps = (
        stepId: string,
        archetype: ReleaseStepType,
        stepName: string,
        scopedDestinations = destinations,
        extraIntegrationIds: string[] = [],
    ) =>
        scopedDestinations.map((destination) => ({
            stepConfigId: `${stepId}-${destination.stepConfigId}`,
            stepName: `${platformName(destination)} ${stepName}`,
            archetype,
            destinationStepConfigIds: [destination.stepConfigId],
            integrationInstallationIds: [...platformIntegrationIds(destination), ...extraIntegrationIds],
            missingRequiredCapabilities: [],
        }));

    const settings = mockWorkflowsAppSettings(appId, 'Voyager', voyagerAppIcon, destinations, {
        legacyAppId: '789',
        stepGroups: [
            {
                archetypeGroups: [
                    {
                        name: 'Kickoff',
                        archetype: ReleaseStepType.Kickoff,
                        steps: [
                            {
                                stepConfigId: 'voyager-kickoff',
                                stepName: 'Kickoff',
                                archetype: ReleaseStepType.Kickoff,
                                destinationStepConfigIds: allDestinationIds,
                                integrationInstallationIds: ['github:12345', 'slack:12345'],
                                missingRequiredCapabilities: [],
                            },
                        ],
                    },
                    {
                        name: 'Feature readiness',
                        archetype: ReleaseStepType.FeatureReadiness,
                        steps: [
                            {
                                stepConfigId: 'voyager-feature-readiness',
                                stepName: 'Feature readiness',
                                archetype: ReleaseStepType.FeatureReadiness,
                                destinationStepConfigIds: allDestinationIds,
                                integrationInstallationIds: ['jira:12345', 'github:12345'],
                                missingRequiredCapabilities: [],
                            },
                        ],
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'CI',
                        archetype: ReleaseStepType.ReleaseCandidate,
                        steps: destinationSteps('voyager-ci', ReleaseStepType.ReleaseCandidate, 'CI', destinations, [
                            'github-ci:12345',
                        ]),
                    },
                    {
                        name: 'Regression testing',
                        archetype: ReleaseStepType.RegressionTesting,
                        steps: destinationSteps(
                            'voyager-regression',
                            ReleaseStepType.RegressionTesting,
                            'Regression testing',
                            nativeDestinations,
                            ['testrail:12345'],
                        ),
                    },
                    {
                        name: 'Beta testing',
                        archetype: ReleaseStepType.BetaTesting,
                        steps: nativeDestinations.map((destination) => ({
                            ...destinationSteps('voyager-beta', ReleaseStepType.BetaTesting, 'Beta testing', [
                                destination,
                            ])[0],
                            integrationInstallationIds:
                                destination.platform === AppPlatform.Ios ? ['apple-beta:12345'] : ['google-beta:12345'],
                        })),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Screenshots',
                        archetype: ReleaseStepType.Screenshots,
                        steps: destinationSteps(
                            'voyager-screenshots',
                            ReleaseStepType.Screenshots,
                            'Screenshots',
                            nativeDestinations,
                        ),
                    },
                    {
                        name: 'Metadata',
                        archetype: ReleaseStepType.Metadata,
                        steps: destinationSteps(
                            'voyager-metadata',
                            ReleaseStepType.Metadata,
                            'Metadata',
                            nativeDestinations,
                            ['crowdin:12345'],
                        ),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Approvals',
                        archetype: ReleaseStepType.Approvals,
                        steps: destinationSteps('voyager-approvals', ReleaseStepType.Approvals, 'Approvals'),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Submission',
                        archetype: ReleaseStepType.Submission,
                        steps: destinationSteps(
                            'voyager-submission',
                            ReleaseStepType.Submission,
                            'Submission',
                            nativeDestinations,
                        ),
                    },
                    {
                        name: 'Store review',
                        archetype: ReleaseStepType.StoreReview,
                        steps: destinationSteps(
                            'voyager-store-review',
                            ReleaseStepType.StoreReview,
                            'Store review',
                            nativeDestinations,
                        ),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Release',
                        archetype: ReleaseStepType.Takeoff,
                        steps: destinationSteps('voyager-release', ReleaseStepType.Takeoff, 'Release'),
                    },
                    {
                        name: 'Rollout',
                        archetype: ReleaseStepType.Rollout,
                        steps: destinations.map((destination) => ({
                            ...destinationSteps('voyager-rollout', ReleaseStepType.Rollout, 'Rollout', [
                                destination,
                            ])[0],
                            integrationInstallationIds: [
                                ...platformIntegrationIds(destination),
                                'sentry:12345',
                                'amplitude:12345',
                            ],
                        })),
                    },
                ],
            },
        ],
    });

    return {
        ...settings,
        integrations: [
            ...settings.integrations,
            {
                id: IntegrationType.GitHubCI,
                installationId: 'github-ci:12345',
                name: 'GitHub Actions',
                providerId: 'github:12345',
                integrationError: null,
                capabilities: [IntegrationCapability.BuildSystem],
                data: {
                    repoName: 'voyager-mobile',
                    repoOwner: 'runway-demo',
                    workflowName: 'Mobile Release',
                    workflowId: 'mobile-release.yml',
                    devWorkflowName: 'Mobile Preview',
                    devWorkflowId: 'mobile-preview.yml',
                    prodWorkflowName: 'Mobile Release',
                    prodWorkflowId: 'mobile-release.yml',
                },
                refreshData: [],
            },
            {
                id: IntegrationType.ExpoEAS,
                installationId: 'expo-eas:12345',
                name: 'Expo EAS',
                providerId: 'expo-eas:12345',
                integrationError: null,
                capabilities: [IntegrationCapability.AppStore],
                data: {
                    projectId: '4db9d6ca-2c5f-4f45-a955-78ac97c3fa31',
                    projectSlug: 'voyager',
                    owner: 'runway-demo',
                    channel: 'production',
                    initialReleasePercentage: 10,
                },
                refreshData: [],
            },
        ],
    };
};

export const mockVoyagerOtaAppSettings = (appId: string): AppSettings => {
    const destinations = [
        DEMO_DESTINATIONS.voyagerOtaIos,
        DEMO_DESTINATIONS.voyagerOtaAndroid,
        DEMO_DESTINATIONS.voyagerOta,
    ];
    const nativeDestinations = destinations.filter(
        (destination) => destination.platform !== AppPlatform.ReactNativeOTA,
    );
    const allDestinationIds = destinations.map((destination) => destination.stepConfigId);
    const platformName = (destination: Destination) =>
        destination.platform === AppPlatform.Ios ? 'iOS'
        : destination.platform === AppPlatform.Android ? 'Android'
        : 'OTA';
    const appStoreIntegrationIds = (destination: Destination) =>
        destination.platform === AppPlatform.Ios ? ['apple:12345']
        : destination.platform === AppPlatform.Android ? ['google:12345']
        : [];
    const destinationSteps = (
        stepId: string,
        archetype: ReleaseStepType,
        stepName: string,
        scopedDestinations = destinations,
        integrationIds: (destination: Destination) => string[] = appStoreIntegrationIds,
    ) =>
        scopedDestinations.map((destination) => ({
            stepConfigId: `voyager-ota-${stepId}-${platformName(destination).toLowerCase()}`,
            stepName: `${platformName(destination)} ${stepName}`,
            archetype,
            destinationStepConfigIds: [destination.stepConfigId],
            integrationInstallationIds: integrationIds(destination),
            missingRequiredCapabilities: [],
        }));

    const settings = mockWorkflowsAppSettings(appId, 'Voyager OTA', voyagerAppIcon, destinations, {
        legacyAppId: '790',
        stepGroups: [
            {
                archetypeGroups: [
                    {
                        name: 'Kickoff',
                        archetype: ReleaseStepType.Kickoff,
                        steps: [
                            {
                                stepConfigId: 'voyager-ota-kickoff',
                                stepName: 'Kickoff',
                                archetype: ReleaseStepType.Kickoff,
                                destinationStepConfigIds: allDestinationIds,
                                integrationInstallationIds: ['github:12345', 'slack:12345'],
                                missingRequiredCapabilities: [],
                            },
                        ],
                    },
                    {
                        name: 'Feature readiness',
                        archetype: ReleaseStepType.FeatureReadiness,
                        steps: [
                            {
                                stepConfigId: 'voyager-ota-feature-readiness',
                                stepName: 'Feature readiness',
                                archetype: ReleaseStepType.FeatureReadiness,
                                destinationStepConfigIds: allDestinationIds,
                                integrationInstallationIds: ['jira:12345', 'github:12345'],
                                missingRequiredCapabilities: [],
                            },
                        ],
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'CI / Staging release',
                        archetype: ReleaseStepType.ReleaseCandidate,
                        steps: destinationSteps('ci', ReleaseStepType.ReleaseCandidate, 'CI', destinations, () => [
                            'github-ci:12345',
                        ]).map((step) =>
                            step.stepName === 'OTA CI' ? { ...step, stepName: 'OTA CI / Staging release' } : step,
                        ),
                    },
                    {
                        name: 'Regression testing',
                        archetype: ReleaseStepType.RegressionTesting,
                        steps: destinationSteps(
                            'regression',
                            ReleaseStepType.RegressionTesting,
                            'Regression testing',
                            destinations,
                            () => ['testrail:12345'],
                        ),
                    },
                    {
                        name: 'Beta testing',
                        archetype: ReleaseStepType.BetaTesting,
                        steps: destinationSteps(
                            'beta',
                            ReleaseStepType.BetaTesting,
                            'Beta testing',
                            destinations,
                            (destination) =>
                                destination.platform === AppPlatform.Ios ? ['apple-beta:12345']
                                : destination.platform === AppPlatform.Android ? ['google-beta:12345']
                                : ['github-ci:12345'],
                        ),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Screenshots',
                        archetype: ReleaseStepType.Screenshots,
                        steps: destinationSteps(
                            'screenshots',
                            ReleaseStepType.Screenshots,
                            'Screenshots',
                            nativeDestinations,
                        ),
                    },
                    {
                        name: 'Metadata',
                        archetype: ReleaseStepType.Metadata,
                        steps: destinationSteps(
                            'metadata',
                            ReleaseStepType.Metadata,
                            'Metadata',
                            nativeDestinations,
                            (destination) => [...appStoreIntegrationIds(destination), 'crowdin:12345'],
                        ),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Approvals',
                        archetype: ReleaseStepType.Approvals,
                        steps: destinationSteps(
                            'approvals',
                            ReleaseStepType.Approvals,
                            'Approvals',
                            destinations,
                            () => [],
                        ),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Submission',
                        archetype: ReleaseStepType.Submission,
                        steps: destinationSteps(
                            'submission',
                            ReleaseStepType.Submission,
                            'Submission',
                            nativeDestinations,
                        ),
                    },
                    {
                        name: 'Store review',
                        archetype: ReleaseStepType.StoreReview,
                        steps: destinationSteps(
                            'store-review',
                            ReleaseStepType.StoreReview,
                            'Store review',
                            nativeDestinations,
                        ),
                    },
                ],
            },
            {
                archetypeGroups: [
                    {
                        name: 'Release',
                        archetype: ReleaseStepType.Takeoff,
                        steps: destinationSteps('release', ReleaseStepType.Takeoff, 'Release', nativeDestinations),
                    },
                    {
                        name: 'Release',
                        archetype: ReleaseStepType.CIDistribution,
                        steps: destinationSteps(
                            'release',
                            ReleaseStepType.CIDistribution,
                            'Release',
                            [DEMO_DESTINATIONS.voyagerOta],
                            () => ['github-ci:12345'],
                        ),
                    },
                    {
                        name: 'Rollout',
                        archetype: ReleaseStepType.Rollout,
                        steps: destinationSteps(
                            'rollout',
                            ReleaseStepType.Rollout,
                            'Rollout',
                            nativeDestinations,
                            (destination) => [
                                ...appStoreIntegrationIds(destination),
                                'sentry:12345',
                                'amplitude:12345',
                            ],
                        ),
                    },
                ],
            },
        ],
    });

    return {
        ...settings,
        integrations: [
            ...settings.integrations,
            {
                id: IntegrationType.GitHubCI,
                installationId: 'github-ci:12345',
                name: 'GitHub Actions',
                providerId: 'github:12345',
                integrationError: null,
                capabilities: [IntegrationCapability.BuildSystem],
                data: {
                    repoName: 'voyager-mobile',
                    repoOwner: 'runway-demo',
                    workflowName: 'OTA Staging Release',
                    workflowId: 'ota-staging.yml',
                    devWorkflowName: 'OTA Staging Release',
                    devWorkflowId: 'ota-staging.yml',
                    prodWorkflowName: 'OTA Production Publish',
                    prodWorkflowId: 'ota-production.yml',
                },
                refreshData: [],
            },
        ],
    };
};

export const mockWhiteLabelAppSettings = (appId: string): AppSettings => {
    const destinations = createWhiteLabelDestinations();
    const allDestinationIds = destinations.map((d) => d.stepConfigId);
    return mockWorkflowsAppSettings(
        appId,
        'Mercury',
        'https://runway-img.s3.us-east-2.amazonaws.com/app-demo-launch.png',
        destinations,
        {
            legacyAppId: '456',
            stepGroups: [
                {
                    archetypeGroups: [
                        {
                            name: 'Kickoff',
                            archetype: ReleaseStepType.Kickoff,
                            steps: [
                                {
                                    stepConfigId: 'kickoff',
                                    stepName: 'Kickoff',
                                    archetype: ReleaseStepType.Kickoff,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: ['github:12345', 'slack:12345'],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                        {
                            name: 'Feature readiness',
                            archetype: ReleaseStepType.FeatureReadiness,
                            steps: [
                                {
                                    stepConfigId: 'feature-readiness',
                                    stepName: 'Feature readiness',
                                    archetype: ReleaseStepType.FeatureReadiness,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: ['jira:12345', 'github:12345'],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'CI',
                            archetype: ReleaseStepType.ReleaseCandidate,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `ci-${dest.stepConfigId}`,
                                stepName: `CI ${dest.name}`,
                                archetype: ReleaseStepType.ReleaseCandidate,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['bitrise:12345', 'github:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Beta testing',
                            archetype: ReleaseStepType.BetaTesting,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `beta-${dest.stepConfigId}`,
                                stepName: `Beta ${dest.name}`,
                                archetype: ReleaseStepType.BetaTesting,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple-beta:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Regression testing',
                            archetype: ReleaseStepType.RegressionTesting,
                            steps: [
                                {
                                    stepConfigId: 'regression-testing',
                                    stepName: 'Regression testing',
                                    archetype: ReleaseStepType.RegressionTesting,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: ['testrail:12345'],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Screenshots',
                            archetype: ReleaseStepType.Screenshots,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `screenshots-${dest.stepConfigId}`,
                                stepName: `Screenshots ${dest.name}`,
                                archetype: ReleaseStepType.Screenshots,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Metadata',
                            archetype: ReleaseStepType.Metadata,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `metadata-${dest.stepConfigId}`,
                                stepName: `Metadata ${dest.name}`,
                                archetype: ReleaseStepType.Metadata,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple:12345', 'crowdin:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Approvals',
                            archetype: ReleaseStepType.Approvals,
                            steps: [
                                {
                                    stepConfigId: 'approvals',
                                    stepName: 'Approvals',
                                    archetype: ReleaseStepType.Approvals,
                                    destinationStepConfigIds: allDestinationIds,
                                    integrationInstallationIds: [],
                                    missingRequiredCapabilities: [],
                                },
                            ],
                        },
                    ],
                },
                {
                    archetypeGroups: [
                        {
                            name: 'Submission',
                            archetype: ReleaseStepType.Submission,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `submission-${dest.stepConfigId}`,
                                stepName: `Submission ${dest.name}`,
                                archetype: ReleaseStepType.Submission,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Review',
                            archetype: ReleaseStepType.StoreReview,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `review-${dest.stepConfigId}`,
                                stepName: `Review ${dest.name}`,
                                archetype: ReleaseStepType.StoreReview,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Release',
                            archetype: ReleaseStepType.Takeoff,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `release-${dest.stepConfigId}`,
                                stepName: `Release ${dest.name}`,
                                archetype: ReleaseStepType.Takeoff,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                        {
                            name: 'Rollout',
                            archetype: ReleaseStepType.Rollout,
                            steps: destinations.map((dest) => ({
                                stepConfigId: `rollout-${dest.stepConfigId}`,
                                stepName: `Rollout ${dest.name}`,
                                archetype: ReleaseStepType.Rollout,
                                destinationStepConfigIds: [dest.stepConfigId],
                                integrationInstallationIds: ['apple:12345', 'sentry:12345', 'amplitude:12345'],
                                missingRequiredCapabilities: [],
                            })),
                        },
                    ],
                },
            ],
        },
    );
};
